import { useEffect } from "react";

const SW_SCRIPT_URL = "/sw.js";
const SW_SCOPE = "/";

/**
 * Enregistre le SW push et force la prise de contrôle si une nouvelle version est en attente.
 */
export async function registerPushServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) {
    return null;
  }

  const registration = await navigator.serviceWorker.register(SW_SCRIPT_URL, {
    scope: SW_SCOPE,
  });

  await registration.update();
  activateWaitingWorker(registration);
  return registration;
}

function activateWaitingWorker(registration: ServiceWorkerRegistration): void {
  if (registration.waiting) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
}

function watchInstallingWorker(registration: ServiceWorkerRegistration): void {
  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;
    if (!installing) return;

    installing.addEventListener("statechange", () => {
      if (
        installing.state === "installed" &&
        navigator.serviceWorker.controller &&
        registration.waiting
      ) {
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
      }
    });
  });
}

/**
 * Recharge la page une fois quand un nouveau SW prend le contrôle.
 */
export function listenForServiceWorkerReload(): () => void {
  if (!("serviceWorker" in navigator)) {
    return () => undefined;
  }

  let reloaded = false;
  const onControllerChange = () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  };

  navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
  return () => navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
}

/**
 * Vérifie les mises à jour du SW (au chargement et au retour sur l'onglet).
 */
export async function checkServiceWorkerUpdate(): Promise<void> {
  if (!("serviceWorker" in navigator)) {
    return;
  }

  const registration = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  if (!registration) {
    return;
  }

  watchInstallingWorker(registration);
  await registration.update();
  activateWaitingWorker(registration);
}

/**
 * Désinscrit tous les service workers (debug / reset complet).
 * Rechargez la page ensuite pour ré-enregistrer un SW propre.
 */
export async function unregisterServiceWorkers(): Promise<number> {
  if (!("serviceWorker" in navigator)) {
    return 0;
  }

  const registrations = await navigator.serviceWorker.getRegistrations();
  const results = await Promise.all(registrations.map((registration) => registration.unregister()));
  return results.filter(Boolean).length;
}

/** Active la détection de mise à jour SW + rechargement automatique. */
export function useServiceWorkerUpdate(): void {
  useEffect(() => {
    if (!("serviceWorker" in navigator)) {
      return undefined;
    }

    const stopReloadListener = listenForServiceWorkerReload();

    void checkServiceWorkerUpdate();
    const onFocus = () => {
      void checkServiceWorkerUpdate();
    };
    window.addEventListener("focus", onFocus);

    return () => {
      window.removeEventListener("focus", onFocus);
      stopReloadListener();
    };
  }, []);
}
